import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2, Check, ArrowRight, AlertCircle, Mail, Shield, Lock, Star, Zap } from "lucide-react";
import { signInSchema, signUpSchema } from "@/lib/validation/authSchemas";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { ForgotPasswordDialog } from "@/components/auth/ForgotPasswordDialog";
import { logger } from "@/utils/logger";
import { hapticSuccess, hapticError } from "@/utils/haptics";
import Logo from "@/components/Logo";
import { useServerConversion } from "@/hooks/useServerConversion";

const testimonials = [
  {
    quote: "Three resignations prevented in the first quarter. The tool paid for itself in a week.",
    name: "Chief People Officer",
    company: "90-person agency",
  },
  {
    quote: "We caught a manager bullying situation that would've cost us 5 team members.",
    name: "Head of People",
    company: "CloudScale Software",
  },
  {
    quote: "Our annual survey said 82% engaged. The first week of honest feedback said otherwise.",
    name: "VP People",
    company: "Series B fintech",
  },
];

const Auth = () => {
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [accountRecoveryPending, setAccountRecoveryPending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const errorAnnouncementRef = useRef<HTMLDivElement>(null);
  
  const { 
    trackSignupPageView, 
    trackSignupStarted, 
    trackSignupCompleted,
    getConversionSessionId 
  } = useServerConversion();

  // Rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isSignUp) {
      trackSignupPageView();
    }
  }, [isSignUp, trackSignupPageView]);

  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          navigate("/dashboard");
        }
      } finally {
        setInitialLoading(false);
      }
    };
    redirectIfAuthenticated();
  }, [navigate]);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email.trim()));
  }, [email]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const schema = isSignUp ? signUpSchema : signInSchema;
    const validation = schema.safeParse({ email, password });

    if (!validation.success) {
      const fieldErrors: any = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0]] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const emailDomain = email.trim().split('@')[1];
        trackSignupStarted(emailDomain);

        const { error, data } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth`,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            await supabase.auth.resetPasswordForEmail(email.trim(), {
              redirectTo: `${window.location.origin}/reset-password`,
            });
            setAccountRecoveryPending(true);
            hapticSuccess();
            toast({
              title: "Account found!",
              description: "We've sent you a password reset link.",
            });
            setLoading(false);
            return;
          }
          throw error;
        }

        if (data.user && !data.user.email_confirmed_at) {
          setEmailVerificationPending(true);
          hapticSuccess();
          toast({
            title: "Verification email sent!",
            description: "Please check your inbox and spam folder.",
          });
          return;
        }

        let sessionInitialized = false;
        let retries = 0;
        const maxRetries = 5;
        
        while (!sessionInitialized && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) {
            logger.error("Session error during initialization", sessionError);
            retries++;
            continue;
          }
          if (session) {
            sessionInitialized = true;
            break;
          }
          retries++;
        }
        
        if (!sessionInitialized) {
          logger.error("Failed to initialize session after signup");
          toast({
            title: "Session Error",
            description: "Please try logging in again.",
            variant: "destructive"
          });
          return;
        }
        
        supabase.functions.invoke('send-welcome-email').catch((err) => {
          logger.error("Failed to send welcome email", err);
        });
        
        if (data.user) {
          trackSignupCompleted(data.user.id);
          getConversionSessionId().then(async (sessionId) => {
            try {
              await supabase.rpc('link_conversion_session_to_user', {
                p_session_id: sessionId,
                p_user_id: data.user!.id
              });
            } catch (err) {
              logger.error("Failed to link conversion session", err);
            }
          });
        }
        
        hapticSuccess();
        toast({
          title: "Account created!",
          description: "Welcome to SendFeedback!",
        });
        navigate("/dashboard");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        
        if (!error && rememberMe) {
          await supabase.auth.refreshSession();
        }

        if (error) {
          if (error.message.includes("Email not confirmed")) {
            setEmailVerificationPending(true);
            toast({
              title: "Email not verified",
              description: "Please verify your email before signing in.",
            });
            setLoading(false);
            return;
          }
          if (error.message.includes("Invalid login credentials")) {
            throw new Error("Invalid email or password. Please check your credentials and try again.");
          }
          throw error;
        }

        hapticSuccess();
        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });
        navigate(redirectPath || "/dashboard");
      }
    } catch (error: unknown) {
      hapticError();
      const errorMessage = error instanceof Error ? error.message : "Authentication failed";
      toast({
        title: "Authentication failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    const newMode = !isSignUp;
    setIsSignUp(newMode);
    setErrors({});
    setPassword("");
    setRememberMe(false);
    setEmailVerificationPending(false);
    setAccountRecoveryPending(false);
    navigate(`/auth?mode=${newMode ? 'signup' : 'signin'}`, { replace: true });
  };

  const handleResendPasswordReset = async () => {
    if (resendCooldown > 0) return;
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast({ title: "Email resent!", description: "Check your inbox for the password reset link." });
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to resend email";
      toast({ title: "Failed to resend email", description: errorMessage, variant: "destructive" });
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: email.trim() });
      if (error) throw error;
      toast({ title: "Email resent!", description: "Check your inbox for the verification link." });
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to resend email";
      toast({ title: "Failed to resend email", description: errorMessage, variant: "destructive" });
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row relative overflow-hidden">
      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0"
        style={{
          background:
            "radial-gradient(900px 520px at 0% 0%, hsl(50 60% 92% / 0.55), transparent 60%), radial-gradient(800px 500px at 100% 100%, hsl(150 30% 90% / 0.4), transparent 60%)",
        }}
      />

      {/* ─── Left panel: brand & social proof (desktop) ─── */}
      <aside className="hidden lg:flex lg:w-[48%] xl:w-[50%] flex-col relative overflow-hidden">
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-foreground" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(700px 400px at 90% 0%, hsl(50 60% 75% / 0.12), transparent 60%), radial-gradient(600px 400px at 0% 100%, hsl(150 40% 50% / 0.12), transparent 60%)",
          }}
        />

        <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-14">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
                <span className="text-primary-foreground text-sm font-semibold">S</span>
              </div>
              <span className="text-background/90 text-lg font-medium tracking-tight">
                Send<span className="text-background/60">feedback</span>
              </span>
            </div>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-full border border-background/15 bg-background/5 backdrop-blur px-4 py-2 text-xs font-medium text-background/70 hover:bg-background/15 transition-all min-h-[40px]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back home
            </button>
          </div>

          {/* Center content */}
          <div className="max-w-lg space-y-10">
            <div className="space-y-5">
              <h1 className="text-4xl xl:text-5xl leading-[1.08] tracking-[-0.025em] font-medium text-background">
                The honest channel{" "}
                <span className="italic font-light text-background/70">your team has been waiting for.</span>
              </h1>
              <p className="text-base xl:text-lg text-background/55 leading-relaxed max-w-md">
                Truly anonymous feedback — by architecture, not policy. No IPs, no logins, no metadata.
              </p>
            </div>

            {/* Rotating testimonial */}
            <div className="rounded-3xl bg-background/8 backdrop-blur-sm border border-background/10 p-6 xl:p-7">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className={`transition-all duration-500 ${
                    activeTestimonial === i
                      ? "opacity-100"
                      : "opacity-0 absolute pointer-events-none"
                  }`}
                >
                  <div className="flex items-center gap-0.5 mb-4">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} className="h-3.5 w-3.5 fill-primary text-primary" />
                    ))}
                  </div>
                  <blockquote className="text-[17px] xl:text-lg text-background/90 leading-relaxed italic mb-4">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <div className="text-sm text-background/50">
                    {t.name} · {t.company}
                  </div>
                </div>
              ))}
              {/* Dots */}
              <div className="flex items-center gap-2 mt-5">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      activeTestimonial === i
                        ? "w-6 bg-primary"
                        : "w-1.5 bg-background/25 hover:bg-background/40"
                    }`}
                    aria-label={`Show testimonial ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Bottom trust signals */}
          <div className="flex flex-wrap items-center gap-6 text-[13px] text-background/45">
            <span className="inline-flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              GDPR · SOC 2 ready
            </span>
            <span className="inline-flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              EU-hosted
            </span>
            <span className="inline-flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              2-minute setup
            </span>
          </div>
        </div>
      </aside>

      {/* ─── Right panel: auth form ─── */}
      <div className="flex-1 flex items-center justify-center px-5 py-8 sm:p-8 lg:p-12 pb-safe relative z-10">
        <div className="w-full max-w-[420px]">
          {/* Mobile header */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <Logo size="sm" />
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-full bg-secondary/80 px-4 py-2 text-sm font-medium text-foreground hover:bg-foreground hover:text-background transition-colors min-h-[44px]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>

          {/* ─── Mode toggle pill ─── */}
          {!accountRecoveryPending && !emailVerificationPending && (
            <div className="flex items-center rounded-full bg-secondary/60 p-1 mb-8">
              <button
                onClick={() => { if (isSignUp) toggleMode(); }}
                className={`flex-1 rounded-full py-2.5 text-sm font-medium transition-all duration-300 min-h-[44px] ${
                  !isSignUp
                    ? "bg-background text-foreground shadow-sm"
                    : "text-foreground/55 hover:text-foreground/80"
                }`}
              >
                Sign in
              </button>
              <button
                onClick={() => { if (!isSignUp) toggleMode(); }}
                className={`flex-1 rounded-full py-2.5 text-sm font-medium transition-all duration-300 min-h-[44px] ${
                  isSignUp
                    ? "bg-background text-foreground shadow-sm"
                    : "text-foreground/55 hover:text-foreground/80"
                }`}
              >
                Create account
              </button>
            </div>
          )}

          {/* Header text */}
          <div className="text-center mb-8">
            <h2 className="text-[28px] sm:text-[32px] leading-[1.1] font-medium text-foreground tracking-[-0.02em] mb-2">
              {accountRecoveryPending
                ? "Account found."
                : emailVerificationPending
                  ? "Check your inbox."
                  : isSignUp
                    ? "Start your free trial"
                    : "Welcome back"}
            </h2>
            <p className="text-[15px] text-muted-foreground leading-relaxed">
              {accountRecoveryPending
                ? `We found an existing account with ${email}`
                : emailVerificationPending
                  ? `We've sent a verification link to ${email}`
                  : isSignUp
                    ? "15 days free · No credit card required"
                    : "Sign in to your dashboard"}
            </p>
          </div>

          {/* ─── Form body ─── */}
          {accountRecoveryPending ? (
            <div className="space-y-6 text-center py-4">
              <div className="flex justify-center">
                <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-9 w-9 text-primary" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  We've sent a password reset link to your email. Click the link to set a new password.
                </p>
              </div>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleResendPasswordReset}
                  disabled={resendCooldown > 0}
                  className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend reset email"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAccountRecoveryPending(false);
                    setIsSignUp(false);
                    navigate('/auth?mode=signin', { replace: true });
                  }}
                  className="w-full min-h-[48px] inline-flex items-center justify-center gap-2 text-sm font-medium text-foreground/65 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </button>
              </div>
            </div>
          ) : emailVerificationPending ? (
            <div className="space-y-6 text-center py-4">
              <div className="flex justify-center">
                <div className="relative h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-9 w-9 text-primary" />
                  <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Click the link in your email to verify your account.
                </p>
              </div>
              <div className="rounded-2xl bg-accent/60 p-4">
                <div className="flex items-start gap-3 text-left">
                  <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Check your spam folder</p>
                    <p className="text-sm text-foreground/65 mt-1 leading-relaxed">
                      Our emails sometimes land in spam or promotions.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0}
                  className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full border border-foreground/15 bg-background text-foreground text-sm font-medium hover:bg-foreground hover:text-background hover:border-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmailVerificationPending(false);
                    setIsSignUp(false);
                    navigate('/auth?mode=signin', { replace: true });
                  }}
                  className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 text-sm font-medium text-foreground/65 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Google Sign-in */}
              <button
                type="button"
                onClick={async () => {
                  setLoading(true);
                  try {
                    const { error } = await supabase.auth.signInWithOAuth({
                      provider: 'google',
                      options: {
                        redirectTo: `${window.location.origin}/dashboard`,
                      },
                    });
                    if (error) throw error;
                  } catch (error: unknown) {
                    hapticError();
                    const errorMessage = error instanceof Error ? error.message : "Google sign-in failed";
                    toast({
                      title: "Sign-in failed",
                      description: errorMessage,
                      variant: "destructive",
                    });
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 min-h-[52px] px-5 rounded-2xl border border-foreground/12 bg-background hover:bg-secondary/80 transition-all duration-200 text-sm font-medium text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                <span>Continue with Google</span>
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-foreground/8" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-background px-4 text-xs font-medium text-foreground/40">or</span>
                </div>
              </div>

              {/* Email form */}
              <form onSubmit={handleAuth} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[13px] font-medium text-foreground/80">Email</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="min-h-[48px] text-[15px] rounded-2xl border-foreground/12 bg-background pr-10 focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-foreground/30"
                      disabled={loading}
                      required
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                    />
                    {isEmailValid && email.length > 0 && (
                      <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-primary animate-in fade-in zoom-in duration-200" />
                    )}
                  </div>
                  {errors.email && (
                    <p id="email-error" className="text-sm text-destructive" role="alert">{errors.email}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[13px] font-medium text-foreground/80">Password</Label>
                    {!isSignUp && (
                      <button
                        type="button"
                        onClick={() => setForgotPasswordOpen(true)}
                        className="text-[13px] font-medium text-primary hover:text-primary/80 transition-colors min-h-[44px] flex items-center"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="min-h-[48px] text-[15px] rounded-2xl border-foreground/12 bg-background pr-12 focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-foreground/30"
                      disabled={loading}
                      required
                      minLength={6}
                      autoComplete={isSignUp ? "new-password" : "current-password"}
                      aria-invalid={!!errors.password}
                      aria-describedby={errors.password ? "password-error" : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground transition-colors w-10 h-10 flex items-center justify-center rounded-full"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p id="password-error" className="text-sm text-destructive" role="alert">{errors.password}</p>
                  )}
                  {isSignUp && password && (
                    <PasswordStrengthIndicator password={password} />
                  )}
                </div>

                {!isSignUp && (
                  <div className="flex items-center space-x-2 pt-1">
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                      disabled={loading}
                      className="h-5 w-5 rounded-xl border-foreground/20"
                    />
                    <Label
                      htmlFor="rememberMe"
                      className="text-[13px] text-foreground/60 cursor-pointer select-none min-h-[44px] flex items-center"
                    >
                      Remember me for 30 days
                    </Label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full min-h-[52px] inline-flex items-center justify-center gap-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:shadow-[0_12px_36px_rgba(62,92,77,0.36)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_24px_rgba(62,92,77,0.25)] mt-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {isSignUp ? "Creating account..." : "Signing in..."}
                    </>
                  ) : (
                    <>
                      {isSignUp ? "Start free trial" : "Sign in"}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>

              {isSignUp && (
                <div className="mt-5 space-y-3">
                  <div className="flex items-center justify-center gap-4 text-xs font-medium text-foreground/50">
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" strokeWidth={2.5} />
                      No card needed
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" strokeWidth={2.5} />
                      Cancel anytime
                    </span>
                  </div>
                  <p className="text-[11px] text-center text-foreground/40 leading-relaxed">
                    By signing up, you agree to our{" "}
                    <Link to="/terms-of-service" className="text-primary underline underline-offset-2">Terms</Link>
                    {" "}and{" "}
                    <Link to="/privacy-policy" className="text-primary underline underline-offset-2">Privacy Policy</Link>
                  </p>
                </div>
              )}

              {/* Mobile-only trust signals */}
              <div className="flex items-center justify-center gap-4 mt-8 text-[11px] text-foreground/40 lg:hidden">
                <span className="inline-flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5" />
                  GDPR ready
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" />
                  EU-hosted
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen}
        currentMode={isSignUp ? 'signup' : 'signin'}
      />

      {/* Hidden error announcement for screen readers */}
      <div ref={errorAnnouncementRef} className="sr-only" role="status" aria-live="polite" />
    </div>
  );
};

export default Auth;
